import React, { useState, useEffect } from "react";
import Chart from "react-apexcharts";
import { getRequest } from "services/api";

const AssetTrendsChart = () => {
  const [trendsData, setTrendsData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dateRange, setDateRange] = useState(() => {
    const now = new Date();
    const currentYear = now.getFullYear();
    const currentMonth = now.getMonth();

    // Start date: 25th of 5 months ago
    const startDate = new Date(currentYear, currentMonth - 5, 26);
    // End date: 25th of current month
    const endDate = new Date(currentYear, currentMonth, 26);

    return {
      start_date: startDate.toISOString().split('T')[0],
      end_date: endDate.toISOString().split('T')[0],
    };
  });

  useEffect(() => {
    const fetchAssetTrends = async () => {
      try {
        setLoading(true);
        const queryString = `?start_date=${dateRange.start_date}&end_date=${dateRange.end_date}`;
        const response = await getRequest(`/ahr/statistics/asset-trends${queryString}`);
        if (response.success) {
          setTrendsData(response.data);
        }
      } catch (error) {
        console.error("Error fetching asset trends:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchAssetTrends();
  }, [dateRange]);

  const handleDateChange = (field, value) => {
    setDateRange(prev => ({
      ...prev,
      [field]: value
    }));
  };

  // Calculate max values for better scaling
  const maxAssets = Math.max(...trendsData.map(item => item.total_assets));
  const maxPrice = Math.max(...trendsData.map(item => parseInt(item.total_price)));

  const chartOptions = {
    chart: {
      type: "line",
      height: 350,
      toolbar: {
        show: false,
      },
      fontFamily: "Inter, sans-serif",
    },
    stroke: {
      curve: "smooth",
      width: [4, 3], // Thicker line for assets, thinner for price
      dashArray: [0, 5], // Solid for assets, dashed for price
    },
    xaxis: {
      categories: trendsData.map(item => {
        // Format month display (e.g., "2025-11" becomes "Nov 2025")
        const [year, month] = item.month.split('-');
        const date = new Date(year, month - 1);
        return date.toLocaleDateString('en-US', { month: 'short', year: 'numeric' });
      }),
      labels: {
        style: {
          fontFamily: "Inter, sans-serif",
          fontSize: "12px",
          fontWeight: 500,
        },
        rotate: 0,
      },
      axisBorder: {
        show: true,
        color: '#e1e5e9',
      },
      axisTicks: {
        show: true,
        color: '#e1e5e9',
      },
    },
    yaxis: [
      {
        title: {
          text: "Total Assets",
          style: {
            fontFamily: "Inter, sans-serif",
            fontSize: "13px",
            fontWeight: 600,
            color: '#206bc4',
          },
        },
        labels: {
          style: {
            fontFamily: "Inter, sans-serif",
            fontSize: "11px",
            fontWeight: 500,
            colors: '#206bc4',
          },
          formatter: function (value) {
            if (value >= 1000) {
              return (value / 1000).toFixed(1) + "K";
            }
            return value.toString();
          },
        },
        min: 0,
        max: maxAssets * 1.1, // Add 10% padding
      },
      {
        opposite: true,
        title: {
          text: "Total Price (Rp)",
          style: {
            fontFamily: "Inter, sans-serif",
            fontSize: "13px",
            fontWeight: 600,
            color: '#2fb344',
          },
        },
        labels: {
          style: {
            fontFamily: "Inter, sans-serif",
            fontSize: "11px",
            fontWeight: 500,
            colors: '#2fb344',
          },
          formatter: function (value) {
            if (value >= 1000000) {
              return (value / 1000000).toFixed(1) + "M";
            } else if (value >= 1000) {
              return (value / 1000).toFixed(0) + "K";
            }
            return value.toString();
          },
        },
        min: 0,
        max: maxPrice * 1.1, // Add 10% padding
      },
    ],
    colors: ["#206bc4", "#2fb344"], // Tabler primary and success colors
    legend: {
      position: "top",
      fontFamily: "Inter, sans-serif",
      fontSize: "13px",
      fontWeight: 500,
      markers: {
        width: 14,
        height: 14,
        radius: 2,
        offsetX: -2,
      },
      itemMargin: {
        horizontal: 20,
        vertical: 8,
      },
      labels: {
        colors: ['#206bc4', '#2fb344'],
      },
    },
    tooltip: {
      style: {
        fontFamily: "Inter, sans-serif",
        fontSize: "12px",
      },
      x: {
        formatter: function(value) {
          return `<strong>${value}</strong>`;
        }
      },
      y: [
        {
          formatter: function (value) {
            return `<span style="color: #206bc4">●</span> <strong>${value}</strong> assets`;
          },
        },
        {
          formatter: function (value) {
            return `<span style="color: #2fb344">●</span> <strong>Rp ${new Intl.NumberFormat("id-ID").format(value)}</strong>`;
          },
        },
      ],
    },
    markers: {
      size: [6, 5], // Larger markers for assets
      colors: ["#206bc4", "#2fb344"],
      strokeColors: ["#ffffff", "#ffffff"],
      strokeWidth: 2,
      hover: {
        size: [8, 7],
      },
      shape: "circle",
    },
    grid: {
      show: true,
      borderColor: '#f8f9fa',
      strokeDashArray: 2,
      xaxis: {
        lines: {
          show: false,
        }
      },
      yaxis: {
        lines: {
          show: true,
          opacity: 0.3,
        }
      },
    },
    dataLabels: {
      enabled: false,
    },
  };

  const chartSeries = [
    {
      name: "Total Assets",
      type: "line",
      data: trendsData.map(item => item.total_assets),
    },
    {
      name: "Total Price",
      type: "line",
      data: trendsData.map(item => parseInt(item.total_price)),
    },
  ];

  if (loading) {
    return (
      <div className="col-lg-7 mb-4">
        <div className="card">
          <div className="card-header">
            <h3 className="card-title">Asset Trends</h3>
          </div>
          <div className="card-body">
            <div className="d-flex justify-content-center align-items-center" style={{ height: "300px" }}>
              <div className="spinner-border" role="status">
                <span className="visually-hidden">Loading...</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="col-lg-7 mb-4">
      <div className="card">
        <div className="card-header">
          <h3 className="card-title">Asset Trends</h3>
          <div className="card-actions">
            <div className="input-group input-group-sm" style={{ width: "auto" }}>
              <input
                type="date"
                className="form-control"
                value={dateRange.start_date}
                onChange={(e) => handleDateChange('start_date', e.target.value)}
              />
              <span className="input-group-text">to</span>
              <input
                type="date"
                className="form-control"
                value={dateRange.end_date}
                onChange={(e) => handleDateChange('end_date', e.target.value)}
              />
            </div>
          </div>
        </div>
        <div className="card-body">
          {trendsData.length > 0 ? (
            <Chart
              options={chartOptions}
              series={chartSeries}
              type="line"
              // height={350}
            />
          ) : (
            <div className="text-center text-muted">
              No asset trends data available
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default AssetTrendsChart;
